"use client";

import {JSX, MutableRefObject, ReactNode, RefObject} from "react";

import {FloatingPortal, Placement} from "@floating-ui/react";

import {useFloatingDropdown} from "@/shared/hooks";

import "./floating-dropdown.scss";

interface FloatingDropdownProps {
    isOpen: boolean;
    onOpenChange?: (open: boolean) => void;
    trigger: ReactNode;
    children: ReactNode;
    placement?: Placement;
    offsetValue?: number;
    shiftPadding?: number;
    className?: string;
    dropdownClassName?: string;
    /**
     * If true, renders dropdown in a portal (document.body) to avoid z-index issues.
     * Z-index stays from `.floating-dropdown__content` in SCSS unless `portalZIndex` is passed.
     */
    usePortal?: boolean;
    /** When set (with `usePortal`), applied as inline z-index on the floating layer. */
    portalZIndex?: number;
    /**
     * Optional ref to the floating dropdown content element.
     * Useful for click-outside detection when using portal.
     */
    floatingRef?: RefObject<HTMLDivElement | null>;
    /**
     * If true, dropdown stays at initial placement and never flips to the opposite side.
     */
    disableFlip?: boolean;
}

export function FloatingDropdown({
    isOpen,
    onOpenChange,
    trigger,
    children,
    placement,
    offsetValue,
    shiftPadding,
    className = "",
    dropdownClassName = "",
    usePortal = false,
    floatingRef,
    disableFlip = false,
    portalZIndex,
}: FloatingDropdownProps): JSX.Element {
    const {refs, floatingStyles, getReferenceProps, getFloatingProps} = useFloatingDropdown({
        isOpen,
        onOpenChange,
        placement,
        offsetValue,
        shiftPadding,
        disableFlip,
    });

    // Combine floating-ui ref with external floatingRef
    const setFloatingRef = (node: HTMLDivElement | null): void => {
        refs.setFloating(node);
        if (floatingRef && "current" in floatingRef) {
            (floatingRef as MutableRefObject<HTMLDivElement | null>).current = node;
        }
    };

    const portalZ = usePortal && portalZIndex !== undefined ? portalZIndex : undefined;
    const dropdownContent = (
        <div
            ref={setFloatingRef}
            style={{
                ...floatingStyles,
                ...(portalZ !== undefined ? {zIndex: portalZ} : {}),
            }}
            {...getFloatingProps()}
            className={`floating-dropdown__content ${dropdownClassName}`}
        >
            {children}
        </div>
    );

    return (
        <div className={`floating-dropdown ${className}`}>
            <div ref={refs.setReference} {...getReferenceProps()}>
                {trigger}
            </div>

            {isOpen && (usePortal ? <FloatingPortal>{dropdownContent}</FloatingPortal> : dropdownContent)}
        </div>
    );
}
